/**
 * Which run the detail pane is showing, held in the URL rather than in component state.
 *
 * Two reasons it is not `useState`. First, a run becomes linkable — the page can be shared at the
 * run someone is actually talking about. Second, and the reason it is a hook rather than a prop: a
 * selection made BY THE PAGE (auto-selecting the newest run, or the one a manual sync just started)
 * and a selection made by the user are otherwise indistinguishable, and they must not behave the
 * same in history. A machine's choice REPLACES so the Back button cannot undo a decision the user
 * never made; a user's click PUSHES so it can.
 *
 * The id is not validated here. Whether it names a run on the current page is a question only the
 * caller — which has the loaded page — can answer, and the answer changes as pages load.
 */
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

const RUN_PARAM = 'run';

export interface RunSelection {
  /** The run id in the URL, or null. Not validated against the loaded page — the caller does that. */
  selectedRunId: string | null;
  /** A user's click. Writes a history entry so Back returns to the previous run. */
  select: (runId: string) => void;
  /** A machine's choice (auto-select, or the run the user just started). REPLACES, never pushes. */
  selectSilently: (runId: string) => void;
}

/**
 * There is deliberately no `clear()`. One shipped, with no production caller and only its own test —
 * the same interface-lie `useRunSync`'s narrowing removed, introduced in the same stage. Nothing on
 * this surface deselects: the two-pane layout always shows SOMETHING beside the table (a run, or one
 * of `RunsPanel`'s four no-selection states), and no control offers "close this run". Both writers
 * below therefore take a run id, and `write` has no delete branch to keep honest. If a real
 * deselecting affordance ever appears, it is three lines back.
 */
export function useRunSelection(): RunSelection {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedRunId = searchParams.get(RUN_PARAM);

  const write = useCallback(
    (runId: string, replace: boolean): void => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(RUN_PARAM, runId);
          return next;
        },
        { replace },
      );
    },
    [setSearchParams],
  );

  return {
    selectedRunId,
    select: useCallback((runId: string) => write(runId, false), [write]),
    selectSilently: useCallback((runId: string) => write(runId, true), [write]),
  };
}
